import type { MedplumClient } from '@medplum/core';
import { Button, Stack, Text } from '@mantine/core';
import { dismissNotify, notify } from '@/lib/notify';
import {
  isSealRetryRefusal,
  SEAL_PENDING_MESSAGE,
  retryAppointmentSeal,
  type SealPendingError,
} from './attendanceFulfillment';

/**
 * Warn that the note is signed but its Appointment update is pending, with a Retry action that
 * re-runs only `seal-appointment` (never re-signs). Stays open until the retry succeeds.
 */
export function notifySealPending(
  medplum: MedplumClient,
  pending: SealPendingError,
  onSealed?: () => void,
): void {
  if (!pending.signedNote.id) return;
  const retry = async (): Promise<void> => {
    try {
      await retryAppointmentSeal(medplum, pending.appointmentId, pending.signedNote, pending.attendance);
      onSealed?.();
      notify({ type: 'success', title: 'Appointment updated', message: 'The signed note sealed the appointment.' });
    } catch (err) {
      // A note that changed or is no longer signed cannot be sealed from this (stale) notice.
      const final = isSealRetryRefusal(err);
      notify({
        type: 'error',
        title: 'Appointment update failed',
        error: err,
        // `error` is a UserFacingError for a refusal, so its own text is shown; this is the fallback.
        message: final
          ? 'Reopen the note to update the appointment.'
          : 'The note is signed. Try the appointment update again.',
      });
      if (!final) notifySealPending(medplum, pending, onSealed);
    }
  };
  const id = notify({
    type: 'warning',
    title: SEAL_PENDING_MESSAGE,
    autoClose: false,
    message: (
      <Stack gap="xs">
        <Text size="sm">The note is signed. The appointment status and attendance have not been updated yet.</Text>
        <Button
          size="xs"
          variant="light"
          onClick={() => {
            dismissNotify(id);
            void retry();
          }}
        >
          Retry appointment update
        </Button>
      </Stack>
    ),
  });
}
